import { HttpInterceptorFn, HttpErrorResponse, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { catchError, throwError, switchMap, Observable } from 'rxjs';
import { inject } from '@angular/core';
import { AuthService, RedirectInProgressError, SIGN_IN_RECOVERY_MESSAGE } from '../services/auth.service';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';

// Header to mark requests that have already been retried after token refresh
const RETRY_HEADER = 'X-Retry-After-Refresh';

// Track if a token refresh is currently in progress to prevent concurrent refresh calls
let isRefreshing = false;
// Queue of failed requests waiting for token refresh to complete
let failedRequestsQueue: Array<{
  request: HttpRequest<any>;
  next: HttpHandlerFn;
  resolve: (value: Observable<any>) => void;
  reject: (reason: any) => void;
  cancelled: boolean;
}> = [];

/**
 * Reset interceptor state - exported for testing and logout scenarios
 */
export function resetInterceptorState(): void {
  isRefreshing = false;
  failedRequestsQueue.forEach(item => item.cancelled = true);
  failedRequestsQueue = [];
}

/**
 * Error interceptor that handles HTTP errors globally
 * Implements automatic token refresh on 401 errors
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Status-derived, user-facing copy. NEVER interpolate `error.message` here:
      // HttpErrorResponse.message is Angular's developer-facing "Http failure
      // response for …" text, which must not leak into anything shown to users.
      let errorMessage: string;

      if (error.error instanceof ErrorEvent || error.status === 0) {
        // Client-side / network error. Real network failures (server
        // unreachable, CORS, DNS, timeout) arrive as an HttpErrorResponse with
        // status 0 whose `error` is a ProgressEvent — not an ErrorEvent — so
        // guard on the status too, otherwise they fall through to a confusing
        // "status 0" generic below.
        errorMessage = 'A network error occurred. Please check your connection and try again.';
      } else {
        // Server-side error
        switch (error.status) {
          case 401:
            // Check if this is a refreshable request
            if (isRefreshableRequest(req)) {
              return handleTokenRefresh(req, next, authService, router, error);
            }
            errorMessage = 'Unauthorized. Please login again.';
            break;
          case 403:
            errorMessage = 'Access forbidden.';
            break;
          case 404:
            errorMessage = 'Resource not found.';
            break;
          case 500:
            errorMessage = 'Internal server error. Please try again later.';
            break;
          default:
            // Any other status (400, 409, 429, 502, …) without ProblemDetails:
            // a clean generic — still no raw framework text.
            errorMessage = `An error occurred (status ${error.status}). Please try again.`;
            break;
        }
      }
      
      // Prefer the backend ProblemDetails `detail` for the user-facing message,
      // falling back to the status-derived friendly copy computed above.
      const problemDetail =
        error.error && typeof error.error === 'object' && typeof error.error.detail === 'string'
          ? error.error.detail
          : null;
      const userFacingMessage = problemDetail ?? errorMessage;

      // Surface the failure (with the original response) to the console for
      // diagnostics. Logged in all builds, matching the interceptor's other
      // console.error calls.
      //
      // IMPORTANT: the TEXT of this log is load-bearing for E2E. The strict
      // ConsoleMonitor (frontend/tests/fixtures/console-monitoring.fixture.ts)
      // suppresses expected noise by matching this output several ways — by exact
      // string (e.g. `=== 'HTTP Error: Resource not found. HttpErrorResponse'` for
      // the 404 `/mission` polling), by `startsWith`, and by per-spec regex
      // `ignorePatterns` (e.g. the offline `A network error occurred …` patterns
      // in frontend/tests/mission-phase-seam-reload.spec.ts). Changing the shape
      // here, even just appending `error.url` for better diagnostics, silently
      // breaks those matches and reds out ~a dozen mission-flow specs at once. If
      // you must change it, update every `HTTP Error` matcher/pattern under
      // frontend/tests/ in the same change (and keep the offline-message text in
      // sync — a stale pattern here was part of the #659 flakiness).
      console.error('HTTP Error:', userFacingMessage, error);

      // Re-throw the ORIGINAL HttpErrorResponse rather than a generic Error, so
      // consumers can still read the response body (`error.error?.detail` /
      // `error.error?.code`) — e.g. the login page's specific message and the
      // EMAIL_NOT_VERIFIED resend affordance. But overwrite `error.message` with
      // a user-facing string first: HttpErrorResponse.message is Angular's
      // developer-facing "Http failure response for …" text, and call sites that
      // display `error.message` (e.g. mission-builder deck-select) would
      // otherwise surface that raw text instead of their friendly fallback.
      (error as { message: string }).message = userFacingMessage;
      return throwError(() => error);
    })
  );
};

/**
 * Determine if a request should trigger token refresh on 401
 * Returns false for refresh endpoint to prevent infinite loops
 * SECURITY: Only refresh tokens for requests to our own API
 */
function isRefreshableRequest(req: HttpRequest<any>): boolean {
  // SECURITY: Only refresh for requests to our own API to prevent token leakage
  if (!req.url.startsWith(environment.apiUrl)) {
    return false;
  }
  
  // Don't refresh if this request was already retried after a refresh (prevents infinite loop)
  if (req.headers.has(RETRY_HEADER)) {
    return false;
  }
  
  // Don't refresh if the request is to the refresh endpoint itself
  if (req.url.includes('/auth/refresh')) {
    return false;
  }
  
  // Don't refresh public auth endpoints
  const publicEndpoints = ['/auth/login', '/auth/register'];
  const urlPath = req.url.substring(environment.apiUrl.length);
  if (publicEndpoints.some(endpoint => urlPath.startsWith(endpoint))) {
    return false;
  }
  
  return true;
}

/**
 * Handle token refresh and retry failed request
 * Implements queue mechanism for concurrent 401 errors
 */
function handleTokenRefresh(
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService,
  router: Router,
  error: HttpErrorResponse
): Observable<any> {
  // If refresh is already in progress, queue this request
  if (isRefreshing) {
    return new Observable(observer => {
      const queueEntry = {
        request: req,
        next: next,
        resolve: (result: Observable<any>) => {
          if (!queueEntry.cancelled) {
            result.subscribe(observer);
          }
        },
        reject: (err: any) => {
          if (!queueEntry.cancelled) {
            observer.error(err);
          }
        },
        cancelled: false
      };
      failedRequestsQueue.push(queueEntry);
      
      // Return teardown function to remove from queue if unsubscribed
      return () => {
        queueEntry.cancelled = true;
        const index = failedRequestsQueue.indexOf(queueEntry);
        if (index > -1) {
          failedRequestsQueue.splice(index, 1);
        }
      };
    });
  }

  // Start refresh process
  isRefreshing = true;

  return authService.refreshToken().pipe(
    switchMap((newToken: string) => {
      // Process all queued requests with the new token
      processQueue(newToken, null);
      
      // Reset flag AFTER processing queue to prevent race condition
      isRefreshing = false;
      
      // Retry the original request with new token and retry marker
      const authReq = req.clone({
        setHeaders: {
          Authorization: `Bearer ${newToken}`,
          [RETRY_HEADER]: 'true'
        }
      });
      return next(authReq);
    }),
    catchError((refreshError) => {
      const sanitizedRefreshError = new Error(SIGN_IN_RECOVERY_MESSAGE);

      // Process queue with error first
      processQueue(null, sanitizedRefreshError);
      
      // Cancel stale retry state after recovery failure so follow-up login attempts
      // start from a clean interceptor state.
      resetInterceptorState();
      
      // If a MSAL redirect (loginRedirect) was already initiated, do NOT call
      // logout/logoutRedirect — that would conflict with the in-progress redirect
      if (refreshError instanceof RedirectInProgressError) {
        console.error('Token refresh interrupted by redirect in progress:', refreshError);
        return throwError(() => sanitizedRefreshError);
      }
      
      // Clear local app auth/session state and route back to login.
      // Do not trigger federated logout redirects here.
      authService.clearAuthStateForRecovery()
        .catch((clearError) => {
          console.error('Failed to clear auth state after token refresh error:', clearError);
        })
        .finally(() => {
          router.navigate(['/login']);
        });
      
      console.error('Token refresh failed:', refreshError);
      return throwError(() => sanitizedRefreshError);
    })
  );
}

/**
 * Process queued requests after token refresh completes (success or failure)
 */
function processQueue(token: string | null, error: any): void {
  failedRequestsQueue.forEach(({ request, next, resolve, reject, cancelled }) => {
    // Skip cancelled requests
    if (cancelled) {
      return;
    }
    
    if (token) {
      // Retry with new token and retry marker to prevent infinite loops
      const authReq = request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
          [RETRY_HEADER]: 'true'
        }
      });
      resolve(next(authReq));
    } else {
      // Reject with error
      reject(error);
    }
  });
  
  // Clear the queue
  failedRequestsQueue = [];
}
